import { ref, watch } from "vue"
// @ts-ignore
import store from "@/store"
import {
  AppNameEnum,
  BaseUser,
  LoginResponse,
  RefreshSessionResponse,
  UserTypeEnum
} from "@vitable/asclepius-api-client-ts"

import { version } from "../../../../package.json"
import { useAsclepius } from "@/composables/api/asclepius"

const APP_NAME: AppNameEnum = AppNameEnum.ProviderDashboard
const APP_VERSION: string = version

export interface StandardAuthenticationFormData {
  emailOrPhone: string
  password: string
  twoFactorToken?: string
  isClinicalAdminIn: boolean
}

export function useVitableStandardAuthentication() {
  const { getAuthenticationApi, callWithResponse, callResult } = useAsclepius()

  const postLoginRedirectPathRef = ref<string>()
  const postLoginRedirectPageNameRef = ref<string>()

  async function standardAuth(
    request: StandardAuthenticationFormData,
    postLoginRedirectPath?: string,
    postLoginRedirectPageName?: string
  ): Promise<void> {
    postLoginRedirectPathRef.value = postLoginRedirectPath
    postLoginRedirectPageNameRef.value = postLoginRedirectPageName
    await callWithResponse(async () => {
      // 2FA completion resubmits the otp_code WITHOUT the password (the V2
      // contract is strict: password or otp_code, never both)
      const response = await getAuthenticationApi().passwordLogin({
        passwordLoginRequest: {
          email_or_phone: request.emailOrPhone,
          user_type: request.isClinicalAdminIn ? UserTypeEnum.ClinicalAdmin : UserTypeEnum.NursePractitioner,
          password: request.twoFactorToken ? undefined : request.password,
          otp_code: request.twoFactorToken,
          app_name: APP_NAME,
          app_version: APP_VERSION
        }
      })

      // An otp challenge is the 2FA prompt; anything else is not completable
      // on this dashboard and reads as an invalid login attempt
      const { login_session, challenge } = response.data
      if (!login_session && challenge?.challenge_type !== "otp") {
        throw new Error("Invalid login attempt, please try again.")
      }
      return response
    })
  }

  watch(callResult, async (newResult) => {
    if (newResult.isSuccess()) {
      const data: LoginResponse = newResult.getData()
      const session = data.login_session
      if (session) {
        store.dispatch("me/login", {
          user: session.user,
          token: session.access_token,
          refreshToken: session.refresh_token,
          postLoginRedirectPath: postLoginRedirectPathRef.value,
          postLoginRedirectPageName: postLoginRedirectPageNameRef.value
        })
      }
    }
  })

  return {
    standardAuth,
    standardAuthResult: callResult
  }
}

export function useRefreshAccessToken() {
  const { getAuthenticationApi, callWithResponse, callResult } = useAsclepius()

  const user = ref<BaseUser>()
  const accessToken = ref<string>()
  const refreshToken = ref<string>()

  async function refreshAccessToken(refresh: string): Promise<void> {
    await callWithResponse(async () =>
      getAuthenticationApi().refreshSession({
        refreshSessionRequest: {
          refresh_token: refresh,
          app_name: APP_NAME,
          app_version: APP_VERSION
        }
      })
    )
  }

  watch(callResult, async (newResult) => {
    if (newResult.isSuccess()) {
      const data: RefreshSessionResponse = newResult.getData()
      user.value = data.user
      accessToken.value = data.access_token
      refreshToken.value = data.refresh_token
    }
  })

  return {
    user,
    accessToken,
    refreshToken,
    refreshAccessToken,
    refreshAccessTokenResult: callResult
  }
}

// The backend never resets a password by session alone, so the signed-in
// user is named explicitly on every call
function getPasswordResetIdentity(): { email_or_phone: string; user_type: UserTypeEnum } {
  const user: BaseUser | undefined = store.getters["me/user"]
  if (!user) throw new Error("Please sign in again to change your password.")
  return { email_or_phone: user.email, user_type: user.user_type }
}

export function useRequestPasswordResetCode() {
  const { getAuthenticationApi, callWithResponse, callResult } = useAsclepius()

  async function requestPasswordResetCode(): Promise<void> {
    await callWithResponse(async () => {
      const response = await getAuthenticationApi().requestPasswordChange({
        requestPasswordChangeRequest: getPasswordResetIdentity()
      })
      // A phone shared by several accounts cannot be resolved; the user must retry with their email
      if (response.data.challenge?.challenge_type !== "otp") {
        throw new Error("Please use the email address on your account to continue.")
      }
      return response
    })
  }

  return {
    requestPasswordResetCode,
    requestPasswordResetCodeResult: callResult
  }
}

export function useUpdatePassword() {
  const { getAuthenticationApi, callWithResponse, callResult } = useAsclepius()

  async function updatePassword(newPassword: string, code: string): Promise<void> {
    await callWithResponse(async () =>
      getAuthenticationApi().resetPassword({
        resetPasswordRequest: { new_password: newPassword, otp_code: code, ...getPasswordResetIdentity() }
      })
    )
  }

  return {
    updatePassword,
    updatePasswordResult: callResult
  }
}
